import { Box, Flex, Text } from '@chakra-ui/react';

export interface SliderProps {
  value: number;
  min: number;
  max: number;
  onChange: (value: number) => void;
  label?: string;
  valueLabel?: string;
  ariaLabel?: string;
  /**
   * Label, track and value on ONE row, the way pdf-to-figma draws its quality control, for
   * a setting that is a footnote to the section above it rather than a section of its own.
   * The default stacks them, which suits a slider that is the point of its own block.
   */
  inline?: boolean;
  /** Granularity of the track. Defaults to the browser's 1. */
  step?: number;
}

/** Figma-styled range slider (native input, themed accent). */
export function Slider({
  value,
  min,
  max,
  onChange,
  label,
  valueLabel,
  ariaLabel,
  inline = false,
  step,
}: SliderProps) {
  const track = (
    <input
      type="range"
      aria-label={ariaLabel ?? label}
      min={min}
      max={max}
      {...(step === undefined ? {} : { step })}
      value={Math.min(Math.max(value, min), max)}
      onChange={(e) => onChange(Number(e.currentTarget.value))}
      style={{
        width: '100%',
        cursor: 'pointer',
        accentColor: 'var(--chakra-colors-figma-accent, #0d99ff)',
      }}
    />
  );

  if (inline) {
    return (
      <Flex align="center" gap="2">
        {label && (
          <Text fontSize="2xs" color="figma.textMuted" flexShrink={0}>
            {label}
          </Text>
        )}
        <Box flex="1" minW="0" display="flex">
          {track}
        </Box>
        {valueLabel && (
          <Text fontSize="2xs" color="figma.textMuted" flexShrink={0}>
            {valueLabel}
          </Text>
        )}
      </Flex>
    );
  }

  return (
    <Box>
      {(label || valueLabel) && (
        <Flex justify="space-between" mb="1.5">
          <Text fontSize="xs" color="figma.textSecondary">
            {label}
          </Text>
          {valueLabel && (
            <Text fontSize="xs" color="figma.textMuted" fontFamily="mono">
              {valueLabel}
            </Text>
          )}
        </Flex>
      )}
      {track}
    </Box>
  );
}
